import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { format } from 'date-fns'
import {
  ArrowLeftIcon,
  UsersIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  UserIcon,
  PhoneIcon,
  DocumentTextIcon,
  FunnelIcon,
  ArrowPathIcon,
  PlayIcon,
  StopIcon,
  AcademicCapIcon,
  BuildingOfficeIcon,
  QrCodeIcon,
  FingerPrintIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '@/hooks/useAuth'
import { 
  getSession,
  checkInStudent,
  checkOutStudent
} from '@/api/sessionsApi'
import toast from 'react-hot-toast'

const SessionAttendanceDetailPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  
  // Main data state
  const [sessionData, setSessionData] = useState<any>(null)
  const [attendanceRecords, setAttendanceRecords] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  
  // Pagination and filtering
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [pageSize] = useState(20)
  const [statusFilter, setStatusFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [methodFilter, setMethodFilter] = useState('all')
  
  // Statistics
  const [statistics, setStatistics] = useState<any>(null)
  
  // Real-time updates
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null)
  const [autoRefresh, setAutoRefresh] = useState(false)

  useEffect(() => {
    if (sessionId) {
      fetchAttendanceData()
    }
  }, [sessionId, currentPage, statusFilter, searchQuery, methodFilter])

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchAttendanceData(false) // Silent refresh
      }, 30000) // Refresh every 30 seconds
      setRefreshInterval(interval)
    } else {
      if (refreshInterval) {
        clearInterval(refreshInterval)
        setRefreshInterval(null)
      }
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval)
      }
    }
  }, [autoRefresh])

  const fetchAttendanceData = async (showLoading = true) => {
    if (showLoading) setLoading(true)
    
    try {
      // Use the regular session endpoint which has the attendance data
      const response = await getSession(sessionId!)
      
      const session = response.data.session
      setSessionData(session)
      
      // Process attendance records with client-side filtering and pagination
      let allAttendance = session.attendance || []
      
      // Apply status filter
      if (statusFilter !== 'all') {
        allAttendance = allAttendance.filter((att: any) => att.status === statusFilter)
      }
      
      // Apply search filter using student data already in attendance records
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase()
        allAttendance = allAttendance.filter((att: any) => (
          // Search by student details (now included in attendance record)
          (att.studentName && att.studentName.toLowerCase().includes(searchLower)) ||
          (att.studentFirstName && att.studentFirstName.toLowerCase().includes(searchLower)) ||
          (att.studentLastName && att.studentLastName.toLowerCase().includes(searchLower)) ||
          (att.studentEmail && att.studentEmail.toLowerCase().includes(searchLower)) ||
          (att.studentNumber && att.studentNumber.toLowerCase().includes(searchLower)) ||
          // Search by attendance data
          att.studentId.toLowerCase().includes(searchLower) ||
          (att.notes && att.notes.toLowerCase().includes(searchLower)) ||
          (att.terminalId && att.terminalId.toLowerCase().includes(searchLower))
        ))
      }
      
      // Calculate pagination
      const totalRecords = allAttendance.length
      const totalPagesCalculated = Math.ceil(totalRecords / pageSize)
      const startIndex = (currentPage - 1) * pageSize
      const endIndex = startIndex + pageSize
      const paginatedAttendance = allAttendance.slice(startIndex, endIndex)
      
      // Student data is now included directly in the attendance records from the enhanced session API
      // No need for separate users API call!
      
      // Enhanced attendance records using student data already in attendance
      const enhancedAttendance = paginatedAttendance.map((att: any) => {
        return {
          attendanceId: att._id,
          student: {
            id: att.studentId,
            firstName: att.studentFirstName || 'Unknown',
            lastName: att.studentLastName || 'Student',
            studentId: att.studentNumber || att.studentId.slice(-8),
            email: att.studentEmail || 'unknown@example.com'
          },
          status: att.status,
          checkInTime: att.checkInTime,
          checkOutTime: att.checkOutTime,
          attendanceHours: att.attendanceHours,
          checkInType: att.checkInType,
          checkOutType: att.checkOutType,
          terminalId: att.terminalId,
          checkOutTerminalId: att.checkOutTerminalId,
          recordedBy: att.recordedBy,
          notes: att.notes,
          statusDisplay: att.checkInTime && att.checkOutTime ? 
            `Present (${att.attendanceHours || 0}h)` :
            att.checkInTime ? 'Checked In' : 
            att.status === 'late' ? 'Late' : 'Absent'
        }
      })
      
      setAttendanceRecords(enhancedAttendance)
      setTotalPages(totalPagesCalculated)
      
      // Calculate statistics
      const stats = {
        totalStudents: session.attendance?.length || 0,
        presentCount: session.attendance?.filter((att: any) => att.status === 'present').length || 0,
        absentCount: session.attendance?.filter((att: any) => att.status === 'absent').length || 0,
        lateCount: session.attendance?.filter((att: any) => att.status === 'late').length || 0,
        checkedInCount: session.attendance?.filter((att: any) => att.checkInTime && !att.checkOutTime).length || 0,
        checkedOutCount: session.attendance?.filter((att: any) => att.checkInTime && att.checkOutTime).length || 0,
        attendanceRate: session.attendance?.length > 0 ? 
          Math.round((session.attendance.filter((att: any) => att.status === 'present').length / session.attendance.length) * 100) : 0,
        totalHours: session.attendance?.reduce((sum: number, att: any) => sum + (att.attendanceHours || 0), 0) || 0,
        methodBreakdown: session.attendance?.reduce((acc: any, att: any) => {
          if (att.checkInType) {
            acc[att.checkInType] = (acc[att.checkInType] || 0) + 1
          }
          return acc
        }, {}) || {}
      }
      
      setStatistics(stats)
    } catch (error) {
      console.error('Failed to fetch attendance:', error)
      if (showLoading) {
        toast.error('Failed to load attendance data')
      }
    } finally {
      if (showLoading) setLoading(false)
    }
  }

  const getMethodIcon = (method: string) => {
    switch (method?.toLowerCase()) {
      case 'rfid': return <QrCodeIcon className="h-4 w-4 text-blue-500" />
      case 'biometric': return <FingerPrintIcon className="h-4 w-4 text-green-500" />
      case 'manual': return <UserIcon className="h-4 w-4 text-orange-500" />
      case 'qr_code': return <DevicePhoneMobileIcon className="h-4 w-4 text-purple-500" />
      default: return <ComputerDesktopIcon className="h-4 w-4 text-gray-500" />
    }
  }

  const getStatusBadge = (status: string) => {
    const config = {
      present: { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon },
      absent: { color: 'bg-red-100 text-red-800', icon: XCircleIcon },
      late: { color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon }
    }
    
    const { color, icon: Icon } = config[status as keyof typeof config] || config.absent
    
    return (
      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <UsersIcon className="h-8 w-8 text-blue-600 mr-3" />
              Session Attendance
            </h1>
            <p className="text-gray-600 mt-1">
              {sessionData?.title || 'Session attendance management and analytics'}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600">Auto-refresh</span>
          </label>
          <button
            onClick={() => fetchAttendanceData()}
            className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800 border border-blue-300 rounded-lg hover:bg-blue-50"
          >
            <ArrowPathIcon className="h-4 w-4 inline mr-1" />
            Refresh
          </button>
        </div>
      </div>

      {/* Session Overview */}
      <div className="bg-white rounded-lg shadow border p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="col-span-2">
            <h3 className="font-semibold text-gray-900 mb-4">Session Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Title:</span>
                <span className="font-medium">{sessionData?.title}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Status:</span>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  sessionData?.status === 'completed' ? 'bg-green-100 text-green-800' :
                  sessionData?.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {sessionData?.status?.replace('_', ' ')}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Scheduled:</span>
                <span className="font-medium">
                  {sessionData?.scheduledStartTime && format(new Date(sessionData.scheduledStartTime), 'MMM dd, yyyy HH:mm')}
                </span>
              </div>
              {sessionData?.actualStartTime && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Started:</span>
                  <span className="font-medium">
                    {format(new Date(sessionData.actualStartTime), 'MMM dd, yyyy HH:mm')}
                  </span>
                </div>
              )}
            </div>
          </div>
          
          <div className="col-span-2">
            <h3 className="font-semibold text-gray-900 mb-4">Attendance Statistics</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-green-700">{statistics?.presentCount || 0}</div>
                <div className="text-sm text-green-600">Present</div>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-red-700">{statistics?.absentCount || 0}</div>
                <div className="text-sm text-red-600">Absent</div>
              </div>
              <div className="bg-yellow-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-yellow-700">{statistics?.lateCount || 0}</div>
                <div className="text-sm text-yellow-600">Late</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-blue-700">{statistics?.checkedInCount || 0}</div>
                <div className="text-sm text-blue-600">Checked In</div>
              </div>
            </div>
            
            {statistics?.attendanceRate !== undefined && (
              <div className="mt-4 text-center">
                <div className="text-lg font-bold text-gray-900">{statistics.attendanceRate}%</div>
                <div className="text-sm text-gray-600">Attendance Rate</div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${statistics.attendanceRate}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow border p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, student ID, email, terminal ID, or notes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="present">Present</option>
            <option value="absent">Absent</option>
            <option value="late">Late</option>
          </select>
          <select
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Methods</option>
            <option value="rfid">RFID</option>
            <option value="biometric">Biometric</option>
            <option value="manual">Manual</option>
            <option value="qr_code">QR Code</option>
          </select>
        </div>
      </div>

      {/* Attendance Records */}
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Attendance Records ({attendanceRecords.length})
            </h3>
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {attendanceRecords.map((record) => (
            <div key={record.attendanceId} className="p-6 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-200 rounded-full flex items-center justify-center">
                    <UserIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">
                      {record.student?.firstName} {record.student?.lastName}
                    </div>
                    <div className="text-sm text-gray-500">
                      {record.student?.studentId} • {record.student?.email}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-6">
                  {/* Check-in Info */}
                  {record.checkInTime && (
                    <div className="text-center">
                      <div className="flex items-center space-x-1 text-sm font-medium text-gray-900">
                        {getMethodIcon(record.checkInType)}
                        <span>Check In</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {format(new Date(record.checkInTime), 'HH:mm:ss')}
                      </div>
                      <div className="text-xs text-gray-500 uppercase">
                        {record.checkInType}
                      </div>
                    </div>
                  )}
                  
                  {/* Check-out Info */}
                  {record.checkOutTime && (
                    <div className="text-center">
                      <div className="flex items-center space-x-1 text-sm font-medium text-gray-900">
                        {getMethodIcon(record.checkOutType)}
                        <span>Check Out</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {format(new Date(record.checkOutTime), 'HH:mm:ss')}
                      </div>
                      <div className="text-xs text-gray-500">
                        {record.attendanceHours}h total
                      </div>
                    </div>
                  )}
                  
                  {/* Status */}
                  <div className="text-center">
                    {getStatusBadge(record.status)}
                    <div className="text-xs text-gray-500 mt-1">
                      {record.statusDisplay}
                    </div>
                  </div>
                  
                  {/* Terminal Info */}
                  {record.terminalId && (
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-900">Terminal</div>
                      <div className="text-xs text-gray-600 font-mono">
                        {record.terminalId}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Notes */}
              {record.notes && (
                <div className="mt-3 pl-16">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-start space-x-2">
                      <DocumentTextIcon className="h-4 w-4 text-yellow-600 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-yellow-800">Notes</div>
                        <div className="text-sm text-yellow-700">{record.notes}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Enhanced Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
              <div className="text-sm text-gray-600">
                Showing <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span> to{' '}
                <span className="font-medium">{Math.min(currentPage * pageSize, attendanceRecords.length)}</span> of{' '}
                <span className="font-medium">{statistics?.totalStudents || 0}</span> attendance records
                {searchQuery && (
                  <span className="ml-2 text-blue-600">
                    (filtered by "{searchQuery}")
                  </span>
                )}
              </div>
              
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-2 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-white"
                  title="First Page"
                >
                  ⟪
                </button>
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-white"
                >
                  Previous
                </button>
                
                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum
                    if (totalPages <= 5) {
                      pageNum = i + 1
                    } else {
                      const start = Math.max(1, currentPage - 2)
                      const end = Math.min(totalPages, currentPage + 2)
                      if (i === 0 && start > 1) {
                        pageNum = 1
                      } else if (i === 4 && end < totalPages) {
                        pageNum = totalPages
                      } else {
                        pageNum = start + i
                        if (pageNum > end) return null
                      }
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-2 text-sm rounded-lg border ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-white'
                        }`}
                      >
                        {pageNum}
                      </button>
                    )
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-white"
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-2 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-white"
                  title="Last Page"
                >
                  ⟫
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Comprehensive Analytics Dashboard */}
      <div className="bg-white rounded-lg shadow border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Session Analytics Dashboard</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          {/* Terminal Usage Analytics */}
          <div>
            <h4 className="font-medium text-gray-700 mb-3">Terminal Usage</h4>
            <div className="space-y-2">
              {statistics?.methodBreakdown && (() => {
                const terminals = attendanceRecords.reduce((acc: any, record) => {
                  if (record.terminalId) {
                    acc[record.terminalId] = (acc[record.terminalId] || 0) + 1
                  }
                  return acc
                }, {})
                
                return Object.entries(terminals).slice(0, 5).map(([terminal, count]) => (
                  <div key={terminal} className="flex justify-between text-sm">
                    <span className="text-gray-600 font-mono">{terminal}:</span>
                    <span className="font-medium">{count} uses</span>
                  </div>
                ))
              })()}
              
              <div className="mt-3 p-2 bg-blue-50 rounded-lg">
                <div className="text-xs text-blue-700">
                  💡 Most used: {(() => {
                    const terminals = attendanceRecords.reduce((acc: any, record) => {
                      if (record.terminalId) {
                        acc[record.terminalId] = (acc[record.terminalId] || 0) + 1
                      }
                      return acc
                    }, {})
                    const mostUsed = Object.entries(terminals).sort(([,a], [,b]) => b - a)[0]
                    return mostUsed ? `${mostUsed[0]} (${mostUsed[1]} times)` : 'None'
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* Timing Analytics */}
          <div>
            <h4 className="font-medium text-gray-700 mb-3">Timing Insights</h4>
            <div className="space-y-2">
              {(() => {
                const checkInTimes = attendanceRecords
                  .filter(r => r.checkInTime)
                  .map(r => new Date(r.checkInTime).getMinutes())
                
                const avgCheckInTime = checkInTimes.length > 0 ? 
                  Math.round(checkInTimes.reduce((sum, time) => sum + time, 0) / checkInTimes.length) : 0
                
                const earlyArrivals = attendanceRecords.filter(r => 
                  r.checkInTime && new Date(r.checkInTime) <= new Date(sessionData?.scheduledStartTime)
                ).length
                
                const lateArrivals = attendanceRecords.filter(r => 
                  r.checkInTime && new Date(r.checkInTime) > new Date(sessionData?.scheduledStartTime)
                ).length
                
                return (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Avg Check-in:</span>
                      <span className="font-medium">{Math.floor(avgCheckInTime / 60)}:{String(avgCheckInTime % 60).padStart(2, '0')}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Early Arrivals:</span>
                      <span className="font-medium text-green-600">{earlyArrivals}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Late Arrivals:</span>
                      <span className="font-medium text-orange-600">{lateArrivals}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Avg Session:</span>
                      <span className="font-medium">
                        {statistics?.totalStudents > 0 ? 
                          Math.round((statistics?.totalHours || 0) / statistics.totalStudents * 10) / 10 : 0}h per student
                      </span>
                    </div>
                  </>
                )
              })()}
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-700 mb-3">Attendance Overview</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total Students:</span>
                <span className="font-medium">{statistics?.totalStudents || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Present:</span>
                <span className="font-medium text-green-600">{statistics?.presentCount || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Absent:</span>
                <span className="font-medium text-red-600">{statistics?.absentCount || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Late:</span>
                <span className="font-medium text-yellow-600">{statistics?.lateCount || 0}</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-700 mb-3">Session Status</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Currently Checked In:</span>
                <span className="font-medium text-blue-600">{statistics?.checkedInCount || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Fully Attended:</span>
                <span className="font-medium text-green-600">{statistics?.checkedOutCount || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total Hours:</span>
                <span className="font-medium">{statistics?.totalHours?.toFixed(1) || 0}h</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-700 mb-3">Check-in Methods Analytics</h4>
            <div className="space-y-3">
              {statistics?.methodBreakdown && Object.entries(statistics.methodBreakdown).map(([method, count]) => {
                const percentage = statistics.totalStudents > 0 ? Math.round((count / statistics.totalStudents) * 100) : 0
                return (
                  <div key={method} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-2">
                        {getMethodIcon(method)}
                        <span className="text-gray-600 capitalize font-medium">{method}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">{count}</span>
                        <span className="text-xs text-gray-500">({percentage}%)</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          method === 'rfid' ? 'bg-blue-500' :
                          method === 'biometric' ? 'bg-green-500' :
                          method === 'manual' ? 'bg-orange-500' : 'bg-purple-500'
                        }`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                )
              })}
              
              {/* Method Distribution Chart */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-xs font-medium text-gray-700 mb-2">Method Distribution</div>
                <div className="flex space-x-1 h-3 rounded-full overflow-hidden">
                  {statistics?.methodBreakdown && Object.entries(statistics.methodBreakdown).map(([method, count], index) => {
                    const percentage = statistics.totalStudents > 0 ? (count / statistics.totalStudents) * 100 : 0
                    return (
                      <div
                        key={method}
                        className={`h-full ${
                          method === 'rfid' ? 'bg-blue-500' :
                          method === 'biometric' ? 'bg-green-500' :
                          method === 'manual' ? 'bg-orange-500' : 'bg-purple-500'
                        }`}
                        style={{ width: `${percentage}%` }}
                        title={`${method}: ${count} (${Math.round(percentage)}%)`}
                      ></div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SessionAttendanceDetailPage